import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtUSD(n) {
  if (n === null || n === undefined) return "—";
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}
function fmtN(n) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}

export default function ProfileOpps({ ccn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/hospitals/${ccn}/utilization/opps`, { withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ source_unavailable: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ccn]);

  if (loading) {
    return <div className="text-xs text-zinc-500 font-mono" data-testid="profile-medicare-opps">loading OPPS…</div>;
  }
  if (!data || data.source_unavailable) {
    return (
      <div className="text-xs text-zinc-500 italic" data-testid="profile-medicare-opps">
        Medicare Outpatient (OPPS): no data — this CCN does not bill OPPS or is below the CMS 11-claim cell-suppression threshold.
      </div>
    );
  }

  const trend = data.trend || [];
  const minP = Math.min(...trend.map(t => t.total_outpatient_payments || 0), 0);
  const maxP = Math.max(...trend.map(t => t.total_outpatient_payments || 0), 1);
  const points = trend.map((t, i) => {
    const x = (i / Math.max(trend.length - 1, 1)) * 100;
    const y = 30 - ((t.total_outpatient_payments - minP) / (maxP - minP || 1)) * 28;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div className="mt-6" data-testid="profile-medicare-opps">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-700">
          Medicare Outpatient (OPPS) — CY{data.calendar_year}
        </h3>
        <span className="text-[10px] text-zinc-500 font-mono" title={data.source}>
          source: CMS · {data.source_uuid?.slice(0, 8)}…
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="border border-zinc-200 rounded p-3 bg-zinc-50">
          <div className="text-[10px] font-mono uppercase text-zinc-500">Total services</div>
          <div className="text-2xl font-semibold text-zinc-900">{fmtN(data.total_outpatient_services)}</div>
        </div>
        <div className="border border-zinc-200 rounded p-3 bg-zinc-50">
          <div className="text-[10px] font-mono uppercase text-zinc-500">Medicare payments</div>
          <div className="text-2xl font-semibold text-zinc-900">{fmtUSD(data.total_outpatient_payments)}</div>
        </div>
        <div className="border border-zinc-200 rounded p-3 bg-zinc-50">
          <div className="text-[10px] font-mono uppercase text-zinc-500">Unique APCs</div>
          <div className="text-2xl font-semibold text-zinc-900">{data.unique_apcs ?? "—"}</div>
        </div>
      </div>

      {trend.length > 1 && (
        <div className="mb-3" data-testid="profile-opps-trend">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">
            5-year payment trend ({trend[0]?.calendar_year}–{trend[trend.length-1]?.calendar_year})
          </div>
          <svg viewBox="0 0 100 30" className="w-full h-10 text-emerald-600">
            <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={points} />
            {trend.map((t, i) => {
              const x = (i / Math.max(trend.length - 1, 1)) * 100;
              const y = 30 - ((t.total_outpatient_payments - minP) / (maxP - minP || 1)) * 28;
              return <circle key={i} cx={x} cy={y} r="1.2" fill="currentColor"><title>{`CY${t.calendar_year}: ${fmtUSD(t.total_outpatient_payments)}`}</title></circle>;
            })}
          </svg>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div data-testid="profile-opps-top-volume">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Top 5 APCs by volume</div>
          <table className="w-full text-xs">
            <tbody>
              {(data.top_5_by_volume || []).map((r, i) => (
                <tr key={r.apc_code} className="border-t border-zinc-100">
                  <td className="py-1 font-mono text-zinc-600 w-12">{r.apc_code}</td>
                  <td className="py-1 text-zinc-800 truncate" title={r.apc_description}>{r.apc_description}</td>
                  <td className="py-1 text-right font-mono">{fmtN(r.services)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div data-testid="profile-opps-top-payment">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">Top 5 APCs by payment</div>
          <table className="w-full text-xs">
            <tbody>
              {(data.top_5_by_payment || []).map((r) => (
                <tr key={r.apc_code} className="border-t border-zinc-100">
                  <td className="py-1 font-mono text-zinc-600 w-12">{r.apc_code}</td>
                  <td className="py-1 text-zinc-800 truncate" title={r.apc_description}>{r.apc_description}</td>
                  <td className="py-1 text-right font-mono">{fmtUSD(r.total_payment)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
